import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    Search,
    Download,
    Inbox,
    CheckCircle2,
    Circle,
    Mail,
    Phone,
    Building2,
    HandHeart,
    RefreshCw,
    ChevronDown,
    ChevronRight,
} from "lucide-react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "unread", label: "Unread" },
    { key: "read", label: "Read" },
];

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = typeof iso === "string" ? new Date(iso) : iso;
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return String(iso);
    }
}

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ total: 0, unread: 0 });
    const [countsLoaded, setCountsLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
        return () => clearTimeout(t);
    }, [query]);

    const loadSubmissions = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (debouncedQuery) params.q = debouncedQuery;
        if (filter === "unread") params.read = false;
        if (filter === "read") params.read = true;
        try {
            const { data } = await api.get("/admin/submissions", { params });
            setItems(data.items || []);
            setCounts({ total: data.total ?? 0, unread: data.unread ?? 0 });
            setCountsLoaded(true);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [debouncedQuery, filter]);

    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    const handleToggleRead = async (item) => {
        const next = !item.read;
        // optimistic update
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: next } : i)));
        setCounts((c) => ({
            ...c,
            unread: c.unread + (next ? -1 : 1),
        }));
        try {
            await api.patch(`/admin/submissions/${item.id}`, { read: next });
        } catch (e) {
            // revert
            setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: !next } : i)));
            setCounts((c) => ({ ...c, unread: c.unread + (next ? 1 : -1) }));
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed.");
        }
    };

    const handleLogout = () => {
        logout();
        toast.message("Signed out.");
        navigate("/admin/login", { replace: true });
    };

    const handleExportCsv = () => {
        const token = getToken();
        if (!token) {
            toast.error("Session expired. Please sign in again.");
            navigate("/admin/login");
            return;
        }
        const url = `${API}/admin/submissions/export.csv?token=${encodeURIComponent(token)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const toggleExpand = (id) =>
        setExpanded((m) => ({ ...m, [id]: !m[id] }));

    const visibleCount = items.length;
    const subtitle = useMemo(() => {
        if (loading) return "Loading…";
        const noun = visibleCount === 1 ? "inquiry" : "inquiries";
        if (filter === "unread") return `${visibleCount} unread ${noun}`;
        if (filter === "read") return `${visibleCount} reviewed ${noun}`;
        return `${visibleCount} ${noun} shown`;
    }, [loading, filter, visibleCount]);

    return (
        <div className="min-h-screen bg-[#F5F3F0] font-body text-[#0033CC]">
            {/* Top bar */}
            <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]" data-testid="admin-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2 font-heading font-bold text-[#0033CC]" data-testid="admin-home-link">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#0033CC] text-white">
                            <HandHeart className="h-5 w-5" />
                        </span>
                        <span className="text-base leading-tight">
                            Neighborhood
                            <span className="block text-xs font-medium text-[#4B5563] tracking-wide">
                                Supply Partners — Admin
                            </span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline text-sm text-[#4B5563]" data-testid="admin-user-email">
                            {user?.email}
                        </span>
                        <button
                            onClick={handleLogout}
                            data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm font-medium text-[#0033CC] hover:bg-[#F5F3F0] transition-colors"
                        >
                            <LogOut className="h-4 w-4" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                {/* Heading + stats */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0033CC]/70">
                            <span className="h-px w-8 bg-[#0033CC]/40" />
                            Inbox
                        </span>
                        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-[#0033CC] tracking-tight">
                            Contact submissions
                        </h1>
                        <p className="mt-2 text-sm text-[#4B5563]" data-testid="admin-subtitle">
                            {subtitle}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center sm:gap-4">
                        <div className="rounded-md bg-white border border-[#E5E7EB] px-4 py-3 min-w-[120px]">
                            <p className="text-xs uppercase tracking-wider text-[#6B7280]">Total</p>
                            <p className="font-heading text-2xl font-bold text-[#0033CC]" data-testid="admin-stat-total">
                                {countsLoaded ? counts.total : "—"}
                            </p>
                        </div>
                        <div className="rounded-md bg-[#0033CC] text-white px-4 py-3 min-w-[120px]">
                            <p className="text-xs uppercase tracking-wider text-white/60">Unread</p>
                            <p className="font-heading text-2xl font-bold" data-testid="admin-stat-unread">
                                {countsLoaded ? counts.unread : "—"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="mt-8 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        {FILTERS.map((f) => {
                            const active = filter === f.key;
                            return (
                                <button
                                    key={f.key}
                                    onClick={() => setFilter(f.key)}
                                    data-testid={`admin-filter-${f.key}`}
                                    className={`rounded-md px-4 py-2 text-sm font-medium transition-colors border ${
                                        active
                                            ? "bg-[#0033CC] text-white border-[#0033CC]"
                                            : "bg-white text-[#0033CC] border-[#E5E7EB] hover:bg-[#EAE6E1]"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex flex-1 lg:flex-none flex-col sm:flex-row gap-3 lg:items-center lg:justify-end">
                        <div className="relative flex-1 sm:flex-none sm:w-80">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search name, email, organization…"
                                data-testid="admin-search-input"
                                className="w-full rounded-md border border-[#E5E7EB] bg-white pl-10 pr-3 py-2.5 text-sm text-[#0033CC] focus:border-[#0033CC] focus:ring-1 focus:ring-[#0033CC] focus:outline-none transition-colors placeholder:text-[#9CA3AF]"
                            />
                        </div>
                        <button
                            onClick={loadSubmissions}
                            data-testid="admin-refresh-button"
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm font-medium text-[#0033CC] hover:bg-[#EAE6E1] transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw className="h-4 w-4" />
                            <span className="sm:hidden lg:inline">Refresh</span>
                        </button>
                        <button
                            onClick={handleExportCsv}
                            data-testid="admin-export-csv"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0033CC] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#1A52E0] transition-colors"
                        >
                            <Download className="h-4 w-4" /> Export CSV
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="mt-8 rounded-md border border-[#E5E7EB] bg-white overflow-hidden" data-testid="admin-submissions-list">
                    {loading ? (
                        <div className="p-12 text-center text-sm text-[#4B5563]" data-testid="admin-loading">
                            Loading submissions…
                        </div>
                    ) : items.length === 0 ? (
                        <div className="p-12 text-center" data-testid="admin-empty-state">
                            <Inbox className="h-10 w-10 mx-auto text-[#9CA3AF]" />
                            <p className="mt-4 font-heading text-lg font-semibold text-[#0033CC]">
                                No submissions
                            </p>
                            <p className="mt-1 text-sm text-[#4B5563]">
                                {debouncedQuery || filter !== "all"
                                    ? "Try clearing your filters or search."
                                    : "When a contact form is submitted it will appear here."}
                            </p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#E5E7EB]">
                            {items.map((item) => {
                                const isOpen = !!expanded[item.id];
                                return (
                                    <li
                                        key={item.id}
                                        data-testid={`admin-submission-${item.id}`}
                                        className={`px-5 sm:px-6 py-5 ${item.read ? "bg-white" : "bg-[#F8F6F2]"}`}
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                                            <button
                                                onClick={() => handleToggleRead(item)}
                                                data-testid={`admin-toggle-read-${item.id}`}
                                                title={item.read ? "Mark as unread" : "Mark as read"}
                                                className="flex-shrink-0 mt-0.5"
                                            >
                                                {item.read ? (
                                                    <CheckCircle2 className="h-5 w-5 text-[#0033CC]" />
                                                ) : (
                                                    <Circle className="h-5 w-5 text-[#1A52E0]" />
                                                )}
                                            </button>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <h3 className={`font-heading text-base sm:text-lg truncate ${item.read ? "font-medium text-[#0033CC]" : "font-semibold text-[#0033CC]"}`}>
                                                            {item.name}
                                                        </h3>
                                                        {!item.read && (
                                                            <span className="inline-flex flex-shrink-0 items-center rounded-full bg-[#0033CC] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                                                                New
                                                            </span>
                                                        )}
                                                    </div>
                                                    <time className="text-xs text-[#6B7280] flex-shrink-0">
                                                        {formatDate(item.created_at)}
                                                    </time>
                                                </div>

                                                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#4B5563]">
                                                    <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0033CC]">
                                                        <Mail className="h-3.5 w-3.5" />
                                                        {item.email}
                                                    </a>
                                                    {item.phone && (
                                                        <a href={`tel:${String(item.phone).replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-[#0033CC]">
                                                            <Phone className="h-3.5 w-3.5" />
                                                            {item.phone}
                                                        </a>
                                                    )}
                                                    {item.organization && (
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <Building2 className="h-3.5 w-3.5" />
                                                            {item.organization}
                                                        </span>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={() => toggleExpand(item.id)}
                                                    data-testid={`admin-expand-${item.id}`}
                                                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#0033CC] hover:text-[#1A52E0]"
                                                >
                                                    {isOpen ? (
                                                        <>
                                                            <ChevronDown className="h-4 w-4" /> Hide message
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ChevronRight className="h-4 w-4" /> View message
                                                        </>
                                                    )}
                                                </button>

                                                {isOpen && (
                                                    <div
                                                        data-testid={`admin-message-${item.id}`}
                                                        className="mt-3 rounded-md bg-[#F5F3F0] border border-[#EAE6E1] p-4 text-sm text-[#0033CC] whitespace-pre-wrap leading-relaxed"
                                                    >
                                                        {item.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
}
